import { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';

interface Props {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  font?: 'sans' | 'display';
  size?: number;
  placeholder?: string;
  ariaLabel?: string;
}

/**
 * Click-to-edit inline rename. Enter commits, Escape reverts. Empty values
 * trigger an error state (shake + red outline) instead of calling onCommit.
 */
export function InlineRename({
  value,
  onCommit,
  font = 'sans',
  size = 17,
  placeholder = 'Untitled',
  ariaLabel,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [err, setErr] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.select();
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) { setErr(true); setTimeout(() => setErr(false), 350); return; }
    setEditing(false);
    if (trimmed !== value) onCommit(trimmed);
  };

  const cancel = () => { setDraft(value); setEditing(false); setErr(false); };

  const style = font === 'display'
    ? { fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: size, letterSpacing: '-0.01em', fontVariationSettings: "'opsz' 18" as React.CSSProperties['fontVariationSettings'] }
    : { fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: size };

  if (editing) {
    return (
      <input
        ref={ref}
        className={'ts-inline-input' + (err ? ' ts-inline-input--error' : '')}
        value={draft}
        placeholder={placeholder}
        style={style}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') cancel();
        }}
      />
    );
  }
  return (
    <span
      className="ts-inline-name"
      style={style}
      tabIndex={0}
      role="button"
      aria-label={ariaLabel ?? `Rename ${value}`}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); } }}
    >
      <span>{value}</span>
      <span className="ts-pencil"><Icon name="pencil" size={12} /></span>
    </span>
  );
}
